import { FC } from "react";
import Box from "@mui/material/Box";
import { VendorSingleSelect } from "@/components/input/VendorInput/VendorSingleSelect";
import { MinMaxEditor, hasMinMaxErrors, getHumanMinMax } from "./MinMaxEditor";
import type {
  ScoringType,
  VendorPenetrationBand,
  MinMaxBand,
} from "@/lib/scoring/types";

// ── Exports for criterion registry ────────────────────────────────────────

export const initialBand: VendorPenetrationBand = { score: 0 };

export function hasErrors(bands: unknown[]): boolean {
  const typed = bands as VendorPenetrationBand[];
  if (typed.length === 0) return true;
  if (!typed[0]?.vendorId) return true;
  return hasMinMaxErrors(typed as MinMaxBand[]);
}

export function getBandSummary(band: unknown): string {
  const b = band as VendorPenetrationBand;
  return getHumanMinMax(b.min, b.max);
}

// ── Component ─────────────────────────────────────────────────────────────

interface Props {
  criterion: ScoringType;
  onChange: (updated: ScoringType) => void;
}

export const VendorPenetrationEditor: FC<Props> = ({ criterion, onChange }) => {
  const bands = criterion.vendorPenetration ?? [];
  const vendorId = bands[0]?.vendorId ?? null;

  const handleVendorChange = (newVendorId: number | null) => {
    onChange({
      ...criterion,
      vendorPenetration:
        bands.length > 0
          ? bands.map((b) => ({ ...b, vendorId: newVendorId ?? undefined }))
          : [{ ...initialBand, vendorId: newVendorId ?? undefined }],
    });
  };

  const handleBandsChange = (newBands: MinMaxBand[]) => {
    onChange({
      ...criterion,
      vendorPenetration: newBands.map((b) => ({
        ...b,
        vendorId: vendorId ?? undefined,
      })),
    });
  };

  return (
    <>
      <Box sx={{ px: 2, mb: 2 }}>
        <VendorSingleSelect
          selectedVendorId={vendorId}
          onVendorSelect={handleVendorChange}
          inputTestId="scoring-vendor-select-input"
        />
      </Box>

      <MinMaxEditor
        bands={bands}
        onChange={handleBandsChange}
        minLabel="Min Installs"
        maxLabel="Max Installs"
        minInputTestId="scoring-vendor-min-input"
        maxInputTestId="scoring-vendor-max-input"
      />
    </>
  );
};
